import pageLoading from './lib/pageLoading.js';
import modalImage from './lib/modalImage.js';
// import { isPage, isMobile } from "./lib/util";
import scrollEffect from "./lib/scrollEffect.js";
// import observer from "./lib/observer.js";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ScrollToPlugin } from "gsap/ScrollToPlugin";
import topValidation from 'top-validation';
import '@/scss/design.scss';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);
ScrollTrigger.config({
  ignoreMobileResize: true,
});

document.addEventListener('DOMContentLoaded', () => {
  /*-------------------------------------------------------------------------------
    初期化処理
  --------------------------------------------------------------------------------*/
  pageLoading();
  modalImage();

  /*-------------------------------------------------------------------------------
    固定ヘッダー補正: ページ内リンク＆初期ハッシュ遷移
  --------------------------------------------------------------------------------*/
  const OFFSET_PC = 180;
  const OFFSET_SP = 120;
  const SP_BREAKPOINT = 768;

  const scrollToHashWithOffset = () => {
    if (!window.location.hash) {
      return;
    }

    const targetId = decodeURIComponent(window.location.hash.replace('#', ''));
    if (!targetId) {
      return;
    }

    const target = document.getElementById(targetId);
    if (!target) {
      return;
    }

    const offset = window.innerWidth <= SP_BREAKPOINT ? OFFSET_SP : OFFSET_PC;
    const targetTop = target.getBoundingClientRect().top + window.pageYOffset - offset;

    window.scrollTo({
      top: Math.max(targetTop, 0),
      behavior: 'auto',
    });
  };

  requestAnimationFrame(() => {
    scrollToHashWithOffset();
  });

  /*-------------------------------------------------------------------------------
    iOS対応: フッターパララックス（SP）
  --------------------------------------------------------------------------------*/
  const initFooterParallaxForSp = () => {
    const parallaxElements = Array.from(document.querySelectorAll('.ft_parallax'));
    if (parallaxElements.length === 0) {
      return;
    }

    const PARALLAX_RANGE = 36;
    let isTicking = false;

    const updateParallax = () => {
      isTicking = false;
      const isSp = window.innerWidth <= SP_BREAKPOINT;
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

      parallaxElements.forEach((el) => {
        if (!isSp) {
          el.style.removeProperty('--ft-parallax-y');
          return;
        }

        const rect = el.getBoundingClientRect();
        const progress = ((viewportHeight - rect.top) / (viewportHeight + rect.height)) * 2 - 1;
        const clamped = Math.max(-1, Math.min(1, progress));
        const shift = clamped * PARALLAX_RANGE;
        el.style.setProperty('--ft-parallax-y', `${shift.toFixed(2)}px`);
      });
    };

    const requestParallaxUpdate = () => {
      if (isTicking) {
        return;
      }

      isTicking = true;
      requestAnimationFrame(updateParallax);
    };

    updateParallax();
    window.addEventListener('scroll', requestParallaxUpdate, { passive: true });
    window.addEventListener('resize', requestParallaxUpdate);
    window.addEventListener('orientationchange', requestParallaxUpdate);
  };

  initFooterParallaxForSp();

  /*-------------------------------------------------------------------------------
    ヘッダーメニュー開閉: SPトリガー
  --------------------------------------------------------------------------------*/
  const header = document.querySelector('.header');
  const trigger = document.querySelector('.hd_trigger');

  if (header && trigger) {
    trigger.addEventListener('click', () => {
      const isOpen = header.classList.toggle('is-open');
      trigger.setAttribute('aria-expanded', String(isOpen));
    });
  }

  /*-------------------------------------------------------------------------------
    スタッフの声モーダル
  --------------------------------------------------------------------------------*/
  const voiceModalTriggers = document.querySelectorAll('.js_voice_modal_open');
  const voiceModals = document.querySelectorAll('.js_voice_modal');
  const voiceModalCloseButtons = document.querySelectorAll('.js_voice_modal_close');
  const VOICE_MODAL_CLOSE_ANIMATION_MS = 300;
  let activeVoiceModal = null;
  let prevBodyOverflow = '';
  const voiceModalCloseTimers = new WeakMap();

  const openVoiceModal = (modal) => {
    if (!modal) {
      return;
    }

    const closeTimer = voiceModalCloseTimers.get(modal);
    if (closeTimer) {
      window.clearTimeout(closeTimer);
      voiceModalCloseTimers.delete(modal);
    }

    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modal.classList.remove('is_closing');
    modal.classList.add('is_open');
    modal.setAttribute('aria-hidden', 'false');
    activeVoiceModal = modal;
  };

  const closeVoiceModal = (modal) => {
    if (!modal) {
      return;
    }

    if (modal.classList.contains('is_closing')) {
      return;
    }

    modal.classList.remove('is_open');
    modal.classList.add('is_closing');
    modal.setAttribute('aria-hidden', 'true');

    const closeTimer = window.setTimeout(() => {
      modal.classList.remove('is_closing');
      document.body.style.overflow = prevBodyOverflow;

      if (activeVoiceModal === modal) {
        activeVoiceModal = null;
      }

      voiceModalCloseTimers.delete(modal);
    }, VOICE_MODAL_CLOSE_ANIMATION_MS);

    voiceModalCloseTimers.set(modal, closeTimer);
  };

  if (voiceModalTriggers.length > 0) {
    voiceModalTriggers.forEach((triggerEl) => {
      triggerEl.addEventListener('click', () => {
        const targetId = triggerEl.getAttribute('data-voice-modal');

        if (!targetId) {
          return;
        }

        const modal = document.getElementById(targetId);
        openVoiceModal(modal);
      });
    });

    voiceModalCloseButtons.forEach((closeButton) => {
      closeButton.addEventListener('click', (event) => {
        const modal = event.currentTarget.closest('.js_voice_modal');
        closeVoiceModal(modal);
      });
    });

    voiceModals.forEach((modal) => {
      modal.addEventListener('click', (event) => {
        if (event.target === modal) {
          closeVoiceModal(modal);
        }
      });
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeVoiceModal) {
        closeVoiceModal(activeVoiceModal);
      }
    });
  }

  /*-------------------------------------------------------------------------------
    FAQ アコーディオン
  --------------------------------------------------------------------------------*/
  const faqTriggers = document.querySelectorAll('.js_faq_trigger');

  faqTriggers.forEach((trigger) => {
    trigger.addEventListener('click', () => {
      const li = trigger.closest('.faq_li');
      if (!li) {
        return;
      }

      const isOpen = li.classList.toggle('is_open');
      trigger.setAttribute('aria-expanded', String(isOpen));

      const answer = li.querySelector('.faq_a');
      if (answer) {
        answer.setAttribute('aria-hidden', String(!isOpen));
      }
    });
  });

  /*-------------------------------------------------------------------------------
    スクロールアニメーション
  --------------------------------------------------------------------------------*/
  const scrollAnimeElements = gsap.utils.toArray('.is_scroll_anime');

  if (scrollAnimeElements.length > 0) {
    gsap.set(scrollAnimeElements, {
      autoAlpha: 0,
      y: 30,
      willChange: 'transform, opacity',
    });

    ScrollTrigger.batch(scrollAnimeElements, {
      start: 'top 87%',
      once: true,
      onEnter: (batch) => {
        gsap.to(batch, {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: 'power1.out',
          stagger: 0.08,
          overwrite: 'auto',
          onComplete: () => {
            gsap.set(batch, {
              willChange: 'auto',
            });
          },
        });
      },
    });
  }

});

/*-------------------------------------------------------------------------------
  TOP Validation
--------------------------------------------------------------------------------*/
if (document.body.classList.contains('contact') ) {
  topValidation();
}